import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type ClientBehaviorStatus = 'Active' | 'Resolved';

export interface ClientBehavior {
    id: string;
    clientId: string;
    behaviorTypeId: string | null;
    behaviorType: string;
    sourceBankId: string | null;
    name: string;
    definition: string;
    topography: string;
    function: string;
    antecedent: string;
    replacement: string;
    proactive: string;
    reactive: string;
    baseline: string;
    reductionGoal: string;
    severityIntensity: string;
    baselineDate: string | null;
    dataCollectionMethod: string;
    status: ClientBehaviorStatus;
}

export interface ClientBehaviorWritePayload {
    clientId?: string;
    name?: string;
    behaviorTypeId?: string | null;
    definition?: string;
    topography?: string;
    function?: string;
    antecedent?: string;
    replacement?: string;
    proactive?: string;
    reactive?: string;
    baseline?: string;
    reductionGoal?: string;
    severityIntensity?: string;
    baselineDate?: string | null;
    dataCollectionMethod?: string;
    status?: ClientBehaviorStatus;
}

interface ListOpts {
    clientId: string;
    status?: ClientBehaviorStatus;
    behaviorTypeId?: string;
    includeResolved?: boolean;
}

async function fetchClientBehaviors(opts: ListOpts): Promise<ClientBehavior[]> {
    const params: Record<string, string> = { clientId: opts.clientId };
    if (opts.status) params.status = opts.status;
    if (opts.behaviorTypeId) params.behaviorTypeId = opts.behaviorTypeId;
    if (opts.includeResolved) params.includeResolved = '1';
    const res = await api.get<ClientBehavior[]>('/client-behaviors', { params });
    return res.data;
}

export function useClientBehaviors(opts: ListOpts) {
    return useQuery({
        queryKey: [
            'client-behaviors',
            opts.clientId,
            opts.status ?? 'any',
            opts.behaviorTypeId ?? 'any',
            opts.includeResolved ? 'with-resolved' : 'active',
        ] as const,
        queryFn: () => fetchClientBehaviors(opts),
        enabled: !!opts.clientId,
        staleTime: 60_000,
    });
}

export function useCreateClientBehavior(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ClientBehaviorWritePayload) => {
            const res = await api.post<ClientBehavior>('/client-behaviors', { ...payload, clientId });
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client-behaviors', clientId] }),
    });
}

export function useUpdateClientBehavior(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ClientBehaviorWritePayload }) => {
            const res = await api.patch<ClientBehavior>(`/client-behaviors/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client-behaviors', clientId] }),
    });
}

export function useRetireClientBehavior(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/client-behaviors/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client-behaviors', clientId] }),
    });
}

export interface SaveToBankResponse {
    bankId: string;
}

export interface SaveToBankError {
    error: string;
    canCreateNew?: boolean;
    status: number;
}

export function useSaveClientBehaviorToBank(clientId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, createNew }: { id: string; createNew?: boolean }) => {
            const res = await api.post<SaveToBankResponse>(
                `/client-behaviors/${id}/save-to-bank`,
                createNew ? { createNew: true } : {},
            );
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client-behaviors', clientId] });
            qc.invalidateQueries({ queryKey: ['behavior-bank'] });
        },
    });
}
